package com.klantenstop.web.styles

import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.px

/**
 * Material 3 type-scale tokens, decoupled from HTML semantics.
 *
 * Base (mobile) sizes and line-heights follow the official M3 reference scale
 * (m3.material.io/styles/typography/type-scale-tokens). `Display`/`Headline` tokens grow at the
 * `MD`/`LG` breakpoints; the MD/LG values themselves are best-guess placeholders (not part of the
 * M3 spec, which is breakpoint-agnostic) and may need design review. `Title`/`Body`/`Label` tokens
 * stay fixed across breakpoints.
 *
 * Usage: `Typography.HeadlineLarge.toModifier()`. Pick a token based on desired visual weight —
 * it is independent from which `Hx` heading tag (semantic rank) you use it with.
 */
object Typography {
    val DisplayLarge = CssStyle {
        base { Modifier.fontSize(57.px).lineHeight(64.px).fontWeight(FontWeight.Normal) }
        Breakpoint.MD { Modifier.fontSize(64.px).lineHeight(72.px) }
        Breakpoint.LG { Modifier.fontSize(72.px).lineHeight(80.px) }
    }

    val DisplayMedium = CssStyle {
        base { Modifier.fontSize(45.px).lineHeight(52.px).fontWeight(FontWeight.Normal) }
        Breakpoint.MD { Modifier.fontSize(52.px).lineHeight(60.px) }
        Breakpoint.LG { Modifier.fontSize(58.px).lineHeight(66.px) }
    }

    val DisplaySmall = CssStyle {
        base { Modifier.fontSize(36.px).lineHeight(44.px).fontWeight(FontWeight.Normal) }
        Breakpoint.MD { Modifier.fontSize(40.px).lineHeight(48.px) }
        Breakpoint.LG { Modifier.fontSize(46.px).lineHeight(54.px) }
    }

    val HeadlineLarge = CssStyle {
        base { Modifier.fontSize(32.px).lineHeight(40.px).fontWeight(FontWeight.Normal) }
        Breakpoint.MD { Modifier.fontSize(36.px).lineHeight(44.px) }
        Breakpoint.LG { Modifier.fontSize(40.px).lineHeight(48.px) }
    }

    val HeadlineMedium = CssStyle {
        base { Modifier.fontSize(28.px).lineHeight(36.px).fontWeight(FontWeight.Normal) }
        Breakpoint.MD { Modifier.fontSize(32.px).lineHeight(40.px) }
        Breakpoint.LG { Modifier.fontSize(36.px).lineHeight(44.px) }
    }

    val HeadlineSmall = CssStyle {
        base { Modifier.fontSize(24.px).lineHeight(32.px).fontWeight(FontWeight.Normal) }
        Breakpoint.MD { Modifier.fontSize(28.px).lineHeight(36.px) }
        Breakpoint.LG { Modifier.fontSize(32.px).lineHeight(40.px) }
    }

    val TitleLarge = CssStyle {
        base { Modifier.fontSize(22.px).lineHeight(28.px).fontWeight(FontWeight.Normal) }
    }

    val TitleMedium = CssStyle {
        base { Modifier.fontSize(16.px).lineHeight(24.px).fontWeight(FontWeight.Medium) }
    }

    val TitleSmall = CssStyle {
        base { Modifier.fontSize(14.px).lineHeight(20.px).fontWeight(FontWeight.Medium) }
    }

    val BodyLarge = CssStyle {
        base { Modifier.fontSize(16.px).lineHeight(24.px).fontWeight(FontWeight.Normal) }
    }

    val BodyMedium = CssStyle {
        base { Modifier.fontSize(14.px).lineHeight(20.px).fontWeight(FontWeight.Normal) }
    }

    val BodySmall = CssStyle {
        base { Modifier.fontSize(12.px).lineHeight(16.px).fontWeight(FontWeight.Normal) }
    }

    val LabelLarge = CssStyle {
        base { Modifier.fontSize(14.px).lineHeight(20.px).fontWeight(FontWeight.Medium) }
    }

    val LabelMedium = CssStyle {
        base { Modifier.fontSize(12.px).lineHeight(16.px).fontWeight(FontWeight.Medium) }
    }

    val LabelSmall = CssStyle {
        base { Modifier.fontSize(11.px).lineHeight(16.px).fontWeight(FontWeight.Medium) }
    }
}
